import { FC, FormEventHandler, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AsyncSearch, Loader, SearchableSelect, SearchableSelectOption } from "components";
import ErrorDialog from "components/organization/dialog/error-dialog";
import SuccessDialog from "components/organization/dialog/success-dialog";
import { useResponse } from "contexts/response";
import { useCustomerAPI } from "hooks/use-customer-api";
import useTimeZone from "hooks/use-timezone";
import { fetchCustomerReportGroups } from "services/customer-report-groups";
import { Customer } from "types";

import './reports.scss';

const DATETIME_LOCAL_FORMAT = 'YYYY-MM-DDTHH:mm';

const CustomReports: FC = () => {
  const { searchCustomers } = useCustomerAPI();
  const { handleError, setSuccess } = useResponse();
  const { dateTimeLocalToUtc, newZonedDayJs, } = useTimeZone();

  const asyncSearchKey = useRef(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [customers, setCustomers] = useState<Map<string, Customer>>(new Map())
  const [reportGroups, setReportGroups] = useState<any[]>([]);

  const [individual, setIndividual] = useState<boolean>(false);
  const [customerId, setCustomerId] = useState<string>('');
  const [reportGroupId, setReportGroupId] = useState<string>('');
  const [endDate, setEndDate] = useState<string>(
    newZonedDayJs().startOf('day').format(DATETIME_LOCAL_FORMAT)
  );

  const reportGroupOptions: SearchableSelectOption[] = useMemo(() => {
    return reportGroups.map(g => ({ label: g.name, value: `${g.id}` }));
  }, [reportGroups]);
  
  useEffect(() => {
    fetchCustomerReportGroups().then(setReportGroups);
  }, []);
  
  const handleCustomerSelect = useCallback((customer?: Customer | null) => {
    if (!customer?.id) return setCustomerId('');
    const id = `${customer.id}`;
    setCustomerId(id);
    setCustomers(prevState => new Map(prevState).set(id, customer));
  }, []);
  
  const downloadReport = useCallback(
    async (reportType: 'activity' | 'inventory') => {
      const init: RequestInit = {
        method: 'POST',
        headers: new Headers({
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'text/csv'
        }),
      };
      const input = new URL(
        `/api/reports/${reportType}`,
        window.location.origin
      );

      input.searchParams.append('endDate', dateTimeLocalToUtc(endDate).slice(0, 19));
      if (customerId) input.searchParams.append('customerId', customerId);
      else input.searchParams.append('reportGroupId', reportGroupId);

      try {
        const response = await fetch(input, init);
        if (!response.ok) handleError(response);

        let blob = await response.blob();
        const headers = response.headers;
        const filename = headers
          .get('Content-Disposition')
          ?.split(';')
          .find(h => h.includes('filename'))
          ?.split('=')
          .at(1)
          ?.replace(/"/g, '');

        const fileUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = filename || reportType;
        link.click();
        link.remove();
        setSuccess('Report downloaded')
      } catch (e) {
        await handleError(e);
      }
    },
    [customerId, dateTimeLocalToUtc, endDate, handleError, reportGroupId, setSuccess]
  );

  const handleReset = useCallback(() => {
    setCustomerId('');
    asyncSearchKey.current++; // clear async search input
    setReportGroupId('');
    setEndDate(newZonedDayJs().startOf('day').toISOString());
    setLoading(false);
  }, [newZonedDayJs])

  const handleSubmit: FormEventHandler = useCallback(
    async (e) => {
      e.preventDefault();

      const reportType = e.nativeEvent instanceof SubmitEvent
        && e.nativeEvent.submitter instanceof HTMLButtonElement
        && e.nativeEvent.submitter.value;

      if (reportType === 'activity' || reportType === 'inventory') { 
        setLoading(true);
        await downloadReport(reportType);
        handleReset();
      }
    },
    [downloadReport, handleReset]
  );

  return (
    <Loader loading={loading}>
      <form
        className="page"
        id="report-form"
        onReset={handleReset}
        onSubmit={handleSubmit}
        data-cy="new-location-form"
      >
        <div className="page-header">
          <h1>CUSTOM REPORT</h1>
          <button className="btn-outline" type="reset">
            RESET
          </button>
        </div>
        <div className="card-primary">
          <div className="card-header mb-5" />
          <div className="card-body">
            <div className="grid-1" id="report-grid">
              <div className="d-flex align-items-center w-100">
                <label className="p-relative">
                  <input
                    className="p-absolute w-100"
                    type="checkbox"
                    checked={!individual}
                    onChange={e => {
                      setIndividual(!e.target.checked);
                      if (e.target.checked) {
                        setCustomerId('');
                        asyncSearchKey.current++; // clear async search input
                      } else setReportGroupId('');
                    }}
                  />
                  <span className="icon-large material-icons text-info-dark">
                    group
                  </span>
                </label>
                <SearchableSelect
                  containerClass="text-field flex-fill"
                  inputClass={!reportGroupId ? 'blank' : ''}
                  data-cy="report-group-select"
                  label="REPORT GROUP"
                  onClear={() => setReportGroupId('')}
                  onValueSelected={setReportGroupId}
                  options={reportGroupOptions}
                  value={reportGroupId ?? ''}
                  required={!individual}
                  disabled={individual}
                />
              </div>
              <div className="d-flex align-items-center w-100">
                <label className="p-relative">
                  <input
                    className="p-absolute w-100"
                    type="checkbox"
                    checked={individual}
                    onChange={e => {
                      setIndividual(e.target.checked)
                      if (!e.target.checked) {
                        setCustomerId('');
                        asyncSearchKey.current++; // clear async search input
                      } else setReportGroupId('');
                    }}
                  />
                  <span className="icon-large material-icons text-info-dark">
                    account_box
                  </span>
                </label>
                  <AsyncSearch<Customer>
                    label="ACCOUNT"
                    value={customers.get(customerId) || null}
                    formatOption={({ name }) => name}
                    formatValue={customer => customer?.name ?? ''}
                    handleSelect={handleCustomerSelect}
                    search={searchCustomers}
                    required={individual}
                    disabled={!individual}
                    key={asyncSearchKey.current}
                  />
              </div>
              <input
                type="datetime-local"
                // value={utcToZonedDateTimeLocal(endDate)}
                value={endDate}
                // onChange={e => setEndDate(dateTimeLocalToUtc(e.target.value))}
                onChange={e => setEndDate(e.target.value)}
              />
              <button className="btn-primary" type="submit" value="activity">
                DOWNLOAD ACTIVITY REPORT
              </button>
              <button className="btn-primary" type="submit" value="inventory">
                DOWNLOAD INVENTORY REPORT
              </button>
            </div>
          </div>
        </div>
      </form>
      <ErrorDialog />
      <SuccessDialog />
    </Loader>
  );
}

export default CustomReports;